import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';
import './SupervisorUpdatePage.css';
import { toast } from '../components/Toast';
import { compressImage, formatFileSize } from '../utils/imageUtils';

function SupervisorUpdatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [projects, setProjects] = useState([]);
  const [projectPage, setProjectPage] = useState(0);
  const [projectTotalPages, setProjectTotalPages] = useState(1);
  const [projectTotalElements, setProjectTotalElements] = useState(0);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const projectPickerRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [compressing, setCompressing] = useState(false);
  
  const [formData, setFormData] = useState({
    projectId: '',
    updateDate: new Date().toISOString().split('T')[0],
    description: '',
    workProgress: 0
  });
  
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);

  // Existing photos state (edit mode)
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState({});
  const [loadingExistingPhotos, setLoadingExistingPhotos] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const existingPhotoUrlsRef = useRef({});

  useEffect(() => {
    existingPhotoUrlsRef.current = existingPhotoUrls;
  }, [existingPhotoUrls]);

  useEffect(() => {
    return () => {
      Object.values(existingPhotoUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    fetchProjects(0, '');
    if (isEditMode) {
      fetchUpdateDetails();
    }
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (projectPickerRef.current && !projectPickerRef.current.contains(e.target)) {
        setProjectDropdownOpen(false);
      }
    };
    if (projectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [projectDropdownOpen]);

  const fetchUpdateDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/supervisor-updates/${id}`);
      const updateData = response.data.data;
      setFormData({
        projectId: updateData.projectId || '',
        updateDate: updateData.updateDate ? updateData.updateDate.split('T')[0] : new Date().toISOString().split('T')[0],
        description: updateData.description || '',
        workProgress: updateData.workProgress || 0
      });
      if (updateData.projectId) {
        setSelectedProject({
          id: updateData.projectId,
          name: updateData.projectName || `Project #${updateData.projectId}`
        });
      }
      fetchExistingPhotos(id);
    } catch (err) {
      console.error('Error fetching supervisor update:', err);
      toast.error('Failed to load supervisor update details');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingPhotos = async (updateId) => {
    setLoadingExistingPhotos(true);
    try {
      const res = await axios.get(`/supervisor-updates/${updateId}/photos`);
      const photosData = res.data?.data || [];
      const list = Array.isArray(photosData) ? photosData : [];
      setExistingPhotos(list);

      list.forEach(p => {
        axios.get(`/supervisor-updates/${updateId}/photos/${p.id}/download`, { responseType: 'blob' })
          .then(r => {
            const url = URL.createObjectURL(r.data);
            setExistingPhotoUrls(prev => ({ ...prev, [p.id]: url }));
          })
          .catch(err => {
            console.error(`Failed to load photo ${p.id}:`, err);
          });
      });
    } catch (err) {
      console.error('Error fetching existing photos:', err);
    } finally {
      setLoadingExistingPhotos(false);
    }
  };

  const handleDeleteExistingPhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }
    setDeletingPhotoId(photoId);
    try {
      await axios.delete(`/supervisor-updates/${id}/photos/${photoId}`);
      toast.success('Photo deleted successfully');
      setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
      if (existingPhotoUrls[photoId]) {
        URL.revokeObjectURL(existingPhotoUrls[photoId]);
        setExistingPhotoUrls(prev => {
          const next = { ...prev };
          delete next[photoId];
          return next;
        });
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      toast.error(err.response?.data?.message || err.response?.data?.error?.message || 'Failed to delete photo');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const fetchProjects = async (page = 0, search = '') => {
    setProjectLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', '20');
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      const response = await axios.get(`/projects?${params.toString()}`);
      const content = response.data?.data?.content || response.data?.content || [];
      const totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1;
      const totalElements = response.data?.data?.totalElements || response.data?.totalElements || 0;
      setProjects(Array.isArray(content) ? content : []);
      setProjectPage(page);
      setProjectTotalPages(totalPages);
      setProjectTotalElements(totalElements);
    } catch (err) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setProjectLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Validate file types
    const validFiles = files.filter(file => {
      const isImage = file.type && file.type.startsWith('image/');
      if (!isImage) {
        toast.error(`${file.name} is not an image file`);
      }
      return isImage;
    });

    if (!validFiles.length) return;

    setCompressing(true);
    try {
      let totalOriginal = 0;
      let totalCompressed = 0;

      const compressedResults = await Promise.all(
        validFiles.map(async (file) => {
          totalOriginal += file.size;
          const result = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.75 });
          totalCompressed += result.compressedSize;
          return result.file;
        })
      );

      const newPreviewUrls = compressedResults.map(file => URL.createObjectURL(file));

      setSelectedPhotos(prev => [...prev, ...compressedResults]);
      setPhotoPreviewUrls(prev => [...prev, ...newPreviewUrls]);

      const savedPercent = totalOriginal > 0 ? Math.round(((totalOriginal - totalCompressed) / totalOriginal) * 100) : 0;
      if (savedPercent > 0) {
        toast.success(`Compressed ${compressedResults.length} photo${compressedResults.length > 1 ? 's' : ''} by ${savedPercent}% (${formatFileSize(totalOriginal)} → ${formatFileSize(totalCompressed)})`);
      } else {
        toast.success(`Added ${compressedResults.length} photo${compressedResults.length > 1 ? 's' : ''} (${formatFileSize(totalCompressed)})`);
      }
    } catch (err) {
      console.error('Error compressing photos:', err);
      toast.error('Failed to compress photos');
    } finally {
      setCompressing(false);
      e.target.value = '';
    }
  };

  const removePhoto = (index) => {
    const newPhotos = selectedPhotos.filter((_, i) => i !== index);
    const newPreviewUrls = photoPreviewUrls.filter((_, i) => i !== index);
    
    // Revoke the removed URL to free memory
    URL.revokeObjectURL(photoPreviewUrls[index]);
    
    setSelectedPhotos(newPhotos);
    setPhotoPreviewUrls(newPreviewUrls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.projectId) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setLoading(true);
    
    try {
      let updateId;
      if (isEditMode) {
        // Update supervisor update
        await axios.put(`/supervisor-updates/${id}`, {
          projectId: parseInt(formData.projectId),
          updateDate: formData.updateDate,
          description: formData.description,
          workProgress: parseInt(formData.workProgress)
        });
        updateId = id;
      } else {
        // Create supervisor update
        const updateResponse = await axios.post('/supervisor-updates', {
          projectId: parseInt(formData.projectId),
          updateDate: formData.updateDate,
          description: formData.description,
          workProgress: parseInt(formData.workProgress)
        });
        updateId = updateResponse.data.data.id;
      }
      
      // Upload photos if any
      if (selectedPhotos.length > 0) {
        setUploadingPhotos(true);
        const photoFormData = new FormData();
        selectedPhotos.forEach(photo => {
          photoFormData.append('photos', photo);
        });
        
        await axios.post(`/supervisor-updates/${updateId}/photos`, photoFormData);
      }
      
      toast.success(isEditMode ? 'Supervisor update updated successfully!' : 'Supervisor update created successfully!');
      
      if (!isEditMode) {
        // Reset form
        setFormData({
          projectId: '',
          updateDate: new Date().toISOString().split('T')[0],
          description: '',
          workProgress: 0
        });
        setSelectedProject(null);
        setSelectedPhotos([]);
        photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
        setPhotoPreviewUrls([]);
      }
      
      // Navigate to My Supervisor Updates page immediately
      navigate('/supervisor-updates/my');
      
    } catch (err) {
      console.error('Error saving supervisor update:', err);
      toast.error(err.response?.data?.error?.message || 'Failed to save supervisor update');
    } finally {
      setLoading(false);
      setUploadingPhotos(false);
    }
  };

  const handleCancel = () => {
    // Clean up preview URLs
    photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    Object.values(existingPhotoUrls).forEach(url => URL.revokeObjectURL(url));
    navigate(-1);
  };

  return (
    <div className="supervisor-update-page">
      <div className="page-header">
        <h1>{isEditMode ? 'Edit Supervisor Update' : 'Create Supervisor Update'}</h1>
        <button className="btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
      </div>

      <div className="update-form-container">
        <form onSubmit={handleSubmit} className="update-form">
          <div className="form-group ts-project-picker-wrap" ref={projectPickerRef}>
            <label>Project *</label>
            <div
              className="ts-project-picker-trigger"
              onClick={() => setProjectDropdownOpen(prev => !prev)}
              tabIndex={0}
            >
              <span className={selectedProject ? 'ts-project-chosen' : 'ts-project-placeholder'}>
                {selectedProject ? selectedProject.name : 'Select a Project'}
              </span>
              <span className="ts-project-arrow">{projectDropdownOpen ? '▲' : '▼'}</span>
            </div>

            {projectDropdownOpen && (
              <div className="ts-project-dropdown">
                <div className="ts-project-search-box">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setProjectSearch(val);
                      fetchProjects(0, val);
                    }}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                  {projectSearch && (
                    <button
                      type="button"
                      className="ts-project-clear-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectSearch('');
                        fetchProjects(0, '');
                      }}
                    >
                      &times;
                    </button>
                  )}
                </div>

                <div className="ts-project-list">
                  <div
                    className={`ts-project-option ${!formData.projectId ? 'is-selected' : ''}`}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, projectId: '' }));
                      setSelectedProject(null);
                      setProjectDropdownOpen(false);
                    }}
                  >
                    <span className="ts-project-option-name"><em>-- Clear Selection --</em></span>
                    {!formData.projectId && <span className="ts-project-check">✓</span>}
                  </div>

                  {projectLoading ? (
                    <div className="ts-project-loading">Loading projects...</div>
                  ) : projects.length === 0 ? (
                    <div className="ts-project-empty">No projects found</div>
                  ) : (
                    projects.map(p => {
                      const isSelected = String(formData.projectId) === String(p.id);
                      return (
                        <div
                          key={p.id}
                          className={`ts-project-option ${isSelected ? 'is-selected' : ''}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, projectId: p.id }));
                            setSelectedProject(p);
                            setProjectDropdownOpen(false);
                          }}
                        >
                          <div className="ts-project-option-info">
                            <span className="ts-project-option-name">{p.name}</span>
                            {p.customerName && (
                              <span className="ts-project-option-sub">{p.customerName}</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {p.status && (
                              <span className={`ts-project-status-tag status-${p.status.toLowerCase()}`}>
                                {p.status}
                              </span>
                            )}
                            {isSelected && <span className="ts-project-check">✓</span>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="ts-project-pagination" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="ts-page-btn"
                    disabled={projectPage <= 0 || projectLoading}
                    onClick={() => fetchProjects(projectPage - 1, projectSearch)}
                  >
                    ◀ Prev
                  </button>
                  <span className="ts-page-info">
                    Page {projectTotalPages > 0 ? projectPage + 1 : 0} of {projectTotalPages}
                    {projectTotalElements > 0 && ` (${projectTotalElements})`}
                  </span>
                  <button
                    type="button"
                    className="ts-page-btn"
                    disabled={projectPage >= projectTotalPages - 1 || projectLoading}
                    onClick={() => fetchProjects(projectPage + 1, projectSearch)}
                  >
                    Next ▶
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="updateDate">Update Date *</label>
            <input
              type="date"
              id="updateDate"
              name="updateDate"
              value={formData.updateDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="6"
              placeholder="Describe the work progress, activities completed, and any issues..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="workProgress">Work Progress (%)</label>
            <div className="progress-input-group">
              <input
                type="range"
                id="workProgress"
                name="workProgress"
                value={formData.workProgress}
                onChange={handleInputChange}
                min="0"
                max="100"
                step="5"
              />
              <span className="progress-value">{formData.workProgress}%</span>
            </div>
          </div>

          {/* Existing Photos (Edit Mode) */}
          {isEditMode && (
            <div className="form-group existing-photos-container">
              <label>Existing Photos ({existingPhotos.length})</label>
              {loadingExistingPhotos ? (
                <div className="su-loading-photos">Loading existing photos...</div>
              ) : existingPhotos.length === 0 ? (
                <div className="su-no-photos">No photos currently attached to this update.</div>
              ) : (
                <div className="photo-preview-grid">
                  {existingPhotos.map(photo => (
                    <div key={photo.id} className="photo-preview-item existing-photo-item">
                      {existingPhotoUrls[photo.id] ? (
                        <a
                          href={existingPhotoUrls[photo.id]}
                          target="_blank"
                          rel="noreferrer"
                          title="Click to view full image"
                        >
                          <img src={existingPhotoUrls[photo.id]} alt={photo.fileName || 'Site photo'} />
                        </a>
                      ) : (
                        <div className="su-photo-placeholder" style={{ height: '150px' }}>⏳</div>
                      )}
                      <button
                        type="button"
                        className="btn-remove-photo btn-delete-existing-photo"
                        title="Delete photo"
                        disabled={deletingPhotoId === photo.id}
                        onClick={() => handleDeleteExistingPhoto(photo.id)}
                      >
                        {deletingPhotoId === photo.id ? '…' : '×'}
                      </button>
                      <span className="photo-name" title={photo.fileName}>
                        {photo.fileName}
                        {photo.fileSize ? (
                          <span className="photo-size-badge">{formatFileSize(photo.fileSize)}</span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="photos">{isEditMode ? 'Add More Site Photos' : 'Site Photos'}</label>
            <input
              type="file"
              id="photos"
              accept="image/*"
              multiple
              disabled={compressing || loading || uploadingPhotos}
              onChange={handlePhotoSelect}
            />
            <small className="form-help">
              Photos are automatically resized and compressed to reduce file size before uploading.
            </small>
            {compressing && (
              <div className="su-loading-photos" style={{ color: '#0284c7' }}>
                ⏳ Compressing and reducing image size...
              </div>
            )}
          </div>

          {photoPreviewUrls.length > 0 && (
            <div className="photo-preview-container">
              <h3>{isEditMode ? 'New Photos to Upload' : 'Selected Photos'} ({photoPreviewUrls.length})</h3>
              <div className="photo-preview-grid">
                {photoPreviewUrls.map((url, index) => (
                  <div key={index} className="photo-preview-item">
                    <img src={url} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="btn-remove-photo"
                      onClick={() => removePhoto(index)}
                    >
                      ×
                    </button>
                    <span className="photo-name" title={selectedPhotos[index]?.name}>
                      {selectedPhotos[index]?.name}
                      {selectedPhotos[index]?.size ? (
                        <span className="photo-size-badge">
                          {formatFileSize(selectedPhotos[index].size)}
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || uploadingPhotos || compressing}
            >
              {loading ? (isEditMode ? 'Saving...' : 'Creating...') : compressing ? 'Compressing Photos...' : uploadingPhotos ? 'Uploading Photos...' : (isEditMode ? 'Save Update' : 'Create Update')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading || uploadingPhotos}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SupervisorUpdatePage;
